import React, { useEffect, Suspense, lazy } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLazyGetUserDetailsQuery } from "./store/services/userLogin";
import { tokenStorage } from "./utils/tokenStorage";
import { startTokenExpiryChecker } from "./utils/tokenExpiryChecker";
import { initTokenBroadcast } from "./utils/tokenBroadcast";
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import "./App.css";
import GameListLayout from "./components/GameListLayout";

// Lazy load route components for code splitting
const Landing = lazy(() => import("./components/Landing"));
const Login = lazy(() => import("./components/Login"));
const Introduction = lazy(() => import("./components/Introduction"));
const GameList = lazy(() => import("./components/GameList"));
const Gameplay = lazy(() => import("./Modules/Gameplay-new"));
const DataAnalysis = lazy(() => import("./Modules/DataAnalysis-new"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundColor: 'var(--background-color)'
  }}>
    <div>Loading...</div>
  </div>
);

function App() {
  const jwt = useSelector((state) => state?.user?.jwt);
  const dispatch = useDispatch();
  const [getUserDetailsQuery] = useLazyGetUserDetailsQuery();

  useEffect(() => {
    // Start token expiry checker
    const cleanupExpiryChecker = startTokenExpiryChecker();
    
    // Initialize token broadcast for multi-tab sync
    const cleanupBroadcast = initTokenBroadcast(dispatch);

    if (jwt && jwt !== "null") {
      console.log("[Tutorial Debug] App: jwt present, calling getUserDetailsQuery (GET /fetch-user)");
      getUserDetailsQuery();
    } else {
      const tokenInStorage = !!tokenStorage.getAccessToken();
      console.log("[Tutorial Debug] App: jwt missing or null, skipping fetch-user", {
        jwtFromRedux: jwt ? "present" : "missing",
        tokenInStorage,
      });
    }

    return () => {
      cleanupExpiryChecker();
      cleanupBroadcast();
    };
  }, [jwt, getUserDetailsQuery])

  return (
    <div className="app-wrapper" style={{ width: '100%', height: "100%", backgroundColor: "var(--background-color)" }}>
      <div className="App">
        <div className="app-route-container">
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={jwt ? <Navigate to="/gamelist" /> : <Login />} />
              <Route path="/" element={<Landing />} />
              {!jwt ?
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="*" element={<Navigate to="/login" />} />
                </> : <>
                  <Route path="/introduction" element={<Introduction />} />
                  <Route path="/gamelist" element={<GameListLayout><GameList /></GameListLayout>} />
                  <Route path="/gamelist/domains" element={<Navigate to="/gamelist" />} />
                  <Route path="/gamelist/category/:categoryId" element={<GameListLayout><GameList /></GameListLayout>} />
                  <Route path="/gamified-data-analytics/:problemId" element={<Gameplay />} />
                  <Route path="/gamified-data-analytics/:problemId/:customProblemSlug" element={<Gameplay />} />
                  <Route path="/analysis" element={<DataAnalysis />} />
                </>
              }
            </Routes>
          </Suspense>
        </Router>
        </div>
      </div>
    </div>
  );
}

export default App;

